import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Clock, Zap, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatPackagePrice } from '../utils/packagePresentation';

export default function DemoModal({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPackages = async () => {
      try {
        const res = await fetch('/api/packages');
        const data = await res.json();

        if (data.status === 'success') {
          const demoPackages = data.packages.filter((pkg) => pkg.demoAvailable);
          setPackages(demoPackages);
        }
      } catch (err) {
        console.error('Failed to fetch packages:', err);
        toast.error(
          t('demoModal.loadError', {
            defaultValue: 'Packages could not be loaded',
          }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [isOpen, t]);

  const handleStartDemo = async () => {
    if (!selectedPackage) {
      toast.error(
        t('demoModal.chooseError', {
          defaultValue: 'Please choose a package',
        }),
      );
      return;
    }

    setStarting(true);
    try {
      localStorage.setItem('demoPackage', selectedPackage.id);
      localStorage.setItem('demoPackageName', selectedPackage.name);
      localStorage.setItem('demoStartTime', Date.now().toString());

      toast.success(
        t('demoModal.startSuccess', {
          package: selectedPackage.name,
          defaultValue: 'Demo started: {{package}}',
        }),
      );

      onClose();
      window.location.href = '/app/?demo=demo';
    } catch (err) {
      console.error('Demo start error:', err);
      toast.error(
        t('demoModal.startError', {
          defaultValue: 'Demo could not be started',
        }),
      );
    } finally {
      setStarting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white"
      >
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="mb-2 text-3xl font-bold">{t('demoModal.title', { defaultValue: 'Start demo' })}</h2>
              <p className="flex items-center gap-2 text-purple-100">
                <Clock size={16} />
                {t('demoModal.subtitle', {
                  defaultValue: 'Try all demo packages free for 30 minutes',
                })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 transition hover:bg-white hover:bg-opacity-20"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-8">
          {loading ? (
            <div className="py-12 text-center">
              <Sparkles className="mx-auto mb-4 animate-spin text-purple-600" size={32} />
              <p>{t('demoModal.loading', { defaultValue: 'Loading packages...' })}</p>
            </div>
          ) : (
            <>
              <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="flex items-center gap-2 text-blue-900">
                  <Zap size={18} className="text-blue-600" />
                  <span>
                    <strong>{t('demoModal.infoTitle', { defaultValue: '30-minute demo:' })}</strong>{' '}
                    {t('demoModal.infoBody', {
                      defaultValue: 'You can test the package features during the demo. Demo data is not kept afterwards.',
                    })}
                  </span>
                </p>
              </div>

              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                {packages.map((pkg) => (
                  <motion.button
                    key={pkg.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`rounded-xl border-2 p-6 text-left transition ${
                      selectedPackage?.id === pkg.id
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 bg-white hover:border-purple-300'
                    }`}
                  >
                    <div className="mb-3 text-4xl">{pkg.icon}</div>
                    <h3 className="mb-2 text-lg font-bold">{pkg.name}</h3>
                    <p className="mb-3 font-bold text-purple-600">
                      {formatPackagePrice(
                        pkg.price,
                        i18n.language,
                        t('comparison.value.free', { defaultValue: 'Free' }),
                      )}
                    </p>

                    <div className="mb-3 space-y-1 text-sm text-gray-600">
                      <p>
                        👥 {pkg.limits.maxRecipients === null ? '∞' : pkg.limits.maxRecipients}{' '}
                        {t('demoModal.recipients', { defaultValue: 'Recipients' })}
                      </p>
                      <p>
                        📅 {pkg.limits.maxEvents === null ? '∞' : pkg.limits.maxEvents}{' '}
                        {t('demoModal.events', { defaultValue: 'Events' })}
                      </p>
                      <p>
                        💬 {pkg.limits.maxMessages === null ? '∞' : pkg.limits.maxMessages}{' '}
                        {t('demoModal.messages', { defaultValue: 'Messages' })}
                      </p>
                    </div>

                    {selectedPackage?.id === pkg.id && (
                      <div className="font-bold text-purple-600">
                        {t('demoModal.selected', { defaultValue: 'Selected' })}
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleStartDemo}
                disabled={!selectedPackage || starting}
                className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 py-4 font-bold text-white transition disabled:opacity-50"
              >
                {starting
                  ? t('demoModal.starting', { defaultValue: 'Starting demo...' })
                  : t('demoModal.start', { defaultValue: 'Start demo' })}
              </motion.button>

              <p className="mt-4 text-center text-sm text-gray-500">
                {t('demoModal.redirectInfo', {
                  defaultValue: 'After the demo starts, you will be redirected to the app.',
                })}
              </p>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
